import React from "react";
import { useTranslation } from "react-i18next";
import { MapPin, Edit, Trash2, Ruler, GitBranch, Calendar, Trophy } from "lucide-react";
import { Track } from "@/types";
import { TrackTypeBadge } from "@/components/tracks/TrackTypeBadge";

interface TrackCardProps {
  track: Track;
  onEdit?: (track: Track) => void;
  onDelete?: (track: Track) => void;
  onView?: (track: Track) => void;
  onLeaderboard?: (track: Track) => void;
}

export const TrackCard: React.FC<TrackCardProps> = ({
  track,
  onEdit,
  onDelete,
  onView,
  onLeaderboard,
}) => {
  const { t } = useTranslation(["pages", "common"]);

  return (
    <div className="bg-white rounded-lg border border-secondary-200 px-4 py-3 sm:px-5 sm:py-3.5">
      {/* Row 1: Name + type badge + actions */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <button
            className="text-base font-semibold text-secondary-900 cursor-pointer hover:text-primary-600 transition-colors truncate text-left"
            onClick={() => onView?.(track)}
          >
            {track.name}
          </button>
          <TrackTypeBadge type={track.type} />
        </div>
        <div className="flex gap-1 flex-shrink-0">
          {onLeaderboard && (
            <button
              onClick={() => onLeaderboard(track)}
              className="p-1.5 text-secondary-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
              title={t("pages:tracks.card.leaderboard")}
              aria-label={t("pages:tracks.card.leaderboard")}
            >
              <Trophy className="h-3.5 w-3.5" />
            </button>
          )}
          {onEdit && (
            <button
              onClick={() => onEdit(track)}
              className="p-1.5 text-secondary-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
              title={t("pages:tracks.card.editTrack")}
              aria-label={t("pages:tracks.card.editTrack")}
            >
              <Edit className="h-3.5 w-3.5" />
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(track)}
              className="p-1.5 text-secondary-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title={t("pages:tracks.card.deleteTrack")}
              aria-label={t("pages:tracks.card.deleteTrack")}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Row 2: Metadata */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-sm text-secondary-500">
        <span className="flex items-center gap-1">
          <MapPin className="h-3.5 w-3.5 text-secondary-400 flex-shrink-0" />
          {track.latitude.toFixed(4)}, {track.longitude.toFixed(4)}
        </span>
        {track.length && (
          <span className="flex items-center gap-1">
            <Ruler className="h-3.5 w-3.5 text-secondary-400 flex-shrink-0" />
            {track.length.toFixed(2)} km
          </span>
        )}
        {track.numberOfTurns && (
          <span className="flex items-center gap-1">
            <GitBranch className="h-3.5 w-3.5 text-secondary-400 flex-shrink-0" />
            {t("pages:tracks.card.turns", { count: track.numberOfTurns })}
          </span>
        )}
        {track.yearOpened && (
          <span className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5 text-secondary-400 flex-shrink-0" />
            {track.yearOpened}
          </span>
        )}
        {track.description && (
          <>
            <span className="text-secondary-400">·</span>
            <span className="truncate max-w-xs">{track.description}</span>
          </>
        )}
      </div>
    </div>
  );
};
